<script>
import { defineAsyncComponent } from 'vue';
import "@common/syncfusionCss";
import { configStore } from "@common/configStore";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import headerImage from '@assets/img/Samaritan-header-mobile.png';
import { controlStore, createStoreIndex } from "@common/control/controlStore";
import { vmsIcons } from "@common/icon/iconRegistry";
import { ddStore } from "@common/ddStore";
import { navigationStore } from "@common/navigation/navigationStore";

const editorSettings = {};

let vmsCfg = {
    cId: "vms",
    cType: "vms",
    cTitle: "VMS",
    cTemplate: `<div oncontextmenu="return false" class="main-grid-wrapper new-VMS-classes">
                    <header class="vms-header">
                        <div class="vms-header__section">
                            <img :src="headerImage" alt="Samaritan" class="header-image" />
                            <h1>{{accountSettings.ACCOUNT_ORG}}</h1>
                        </div>

                        <global-hierarchy-filters c-id="globalHierarchyFilters"></global-hierarchy-filters>
                        <div class="vms-header__section user-menu__controls">
                            <control name="HELP_MENU" :items="helpMenu"></control>
                            <div class="separator"></div>
                            <control name="USER_MENU" :items="userMenu" :label="userNameLabelTemplate"></control>
                        </div>
                    </header>

                    <div class="main-content-wrapper">
                        <nav-bar
                            ref="navBar"
                            :items="leftMenu"
                            :active-item="activeNavButton"
                            :show-labels="navLabels"
                            :admin-items="adminDrawerItems"
                            @navigate="onNavBarNavigate"
                            @toggle-labels="navLabels = !navLabels"
                            @commit="commitNavItem"
                            @action="doAction"
                        />

                        <div class="main-grid-content">
                            <transition name="content-fade">
                                <div v-if="!isLoading" class="main-grid-content-inner">
                                    <vms-main-grid
                                        v-if="psp && psp !== 'dashboard'"
                                        :c-id="'vmsMainGrid__' + entity"
                                        :prop-psp="psp"
                                        @close-panel="onClosePanel"
                                        ref="vmsMainGrid"
                                    ></vms-main-grid>
                                    <layout-container v-if="psp === 'dashboard'" :c-id="accountSettings.ACCOUNT_EC_DASHBOARD_ID"></layout-container>
                                </div>
                            </transition>
                            <div v-if="isLoading" class="main-grid-loading">
                                <div class="loading-spinner"></div>
                            </div>
                        </div>

                        <app-bar :panels="rightMenu" />

                    </div>
                    <automation-will-send-emails></automation-will-send-emails>
                </div>
                `,
    controls: [
        {
            name: 'HELP_MENU',
            type: 'dropdownbutton',
            iconCss: 'fa-regular fa-circle-question',
            cssClass: 'e-caret-hide',
            width: '28px',
            onselect: 'doAction'
        },
        {
            name: 'USER_MENU',
            type: 'dropdownbutton',
            width: 'fit-content',
            onbeforeitemrender: 'onBeforeItemRender',
            onselect: 'doAction',
            hideTitle: true
        },
    ]
};

const vmsCmp = {
    name: 'vms',
    preInitControls: true,
    editorSettings: editorSettings,
    props: {
        entity: {
            default: false
        },
        entityId: {
            default: false
        }
    },
    data() {
        return {
            leftMenu: [],
            userMenu: [],
            helpMenu: [],
            adminDrawerItems: [],
            activeNavButton: null,
            headerImage: headerImage,
            rightMenu: [
                { name: 'NAV_HISTORY', label: 'Navigation History', icon: vmsIcons['NAV_HISTORY'] },
                { name: 'NAV_MINIMIZED', label: 'Minimized Windows', icon: vmsIcons['NAV_MINIMIZED'] },
            ],
            reloading: false,
            ddLoading: true,
            navLabels: true,
            initialsHeaderColors: [
                '#245386',
                '#DB2E2F',
                '#3B855D',
                '#6E3481',
                '#F19E41',
                '#FF4CA4',
                '#D5A300'
            ]
        };
    },
    computed: {
        psp() {
            return library.getPspByAbbrev(this.entity);
        },
        isLoading() {
            return this.ddLoading || this.reloading;
        },
        userNameLabelTemplate() {
            if (typeof this.userStore.coordinator !== 'object') {
                return '';
            }

            let clientName = null;
            let clientInitials = null;
            let clientID = null;
            let clientEmail = null;
            let initialsColor = null;

            if (
                typeof this.userStore.coordinator.CLIENT_NAME === 'string'
                && this.userStore.coordinator.CLIENT_NAME
            ) {
                clientName = this.userStore.coordinator.CLIENT_NAME;
            } else if (
                typeof this.userStore.coordinator.CLIENT_USER_NAME === 'string'
                && this.userStore.coordinator.CLIENT_USER_NAME
            ) {
                clientName = this.userStore.coordinator.CLIENT_USER_NAME;
            }

            if (
                typeof this.userStore.coordinator.CLIENT_ID === 'number'
                && this.userStore.coordinator.CLIENT_ID
            ) {
                clientID = this.userStore.coordinator.CLIENT_ID;
            }

            if (
                typeof this.userStore.coordinator.CLIENT_EMAIL === 'string'
                && this.userStore.coordinator.CLIENT_EMAIL
            ) {
                clientEmail = this.userStore.coordinator.CLIENT_EMAIL;
            }

            clientInitials = clientName
                ? clientName.trim().split(/\s+/).slice(0, 2).map((name) => name[0].toUpperCase())
                    .join('')
                : '';

            if (this.initialsHeaderColors.length && clientID) {
                initialsColor = this.initialsHeaderColors[clientID % this.initialsHeaderColors.length];
            }

            return `
                <div class="header-user-menu">
                    <div class="user-initials" style="` + (initialsColor ? 'background: ' + initialsColor : '') + `">
                        <span>` + clientInitials + `</span>
                    </div>
                    <div class="user-labels">
                        <div class="user-name"><span>` + clientName + `</span></div>
                        ` + (clientEmail ? `<div class="user-email"><span>` + clientEmail + `</span></div>` : ``) + `
                    </div>
                </div>
            `;
        },
    },
    watch: {
        '$route'(to) {
            let entity = to.params?.propEntity;
            if (entity) {
                this.activeNavButton = 'COORD_' + entity.toUpperCase();
            }
        }
    },
    methods: {
        processTab(ev, button) {
            if (button?.name?.indexOf('COORD_') === -1) return;
            let tmpPspAbbrev = button?.name?.replace('COORD_', '');
            if (!tmpPspAbbrev) return;
            let goTo = '/' + tmpPspAbbrev.toLowerCase();
            if (this.$router.currentRoute.value.path === goTo) return;
            this.reloading = true;
            this.$nextTick(() => {
                this.$router.push({
                    path: '/' + tmpPspAbbrev.toLowerCase(),
                    query: this.$router.currentRoute.value.query
                });
                setTimeout(() => {
                    this.reloading = false;
                }, 500);
            });
        },
        onNavBarNavigate(item) {
            this.activeNavButton = item.name;
            this.processTab(null, item);
        },
        onClosePanel() {
            this.$refs.navBar?.closeDrawer();
        },
        commitNavItem(navItem) {
            this.activeNavButton = navItem.name;
            this.processTab(null, navItem);
        },
        async doAction(ev) {
            if (!ev?.item?.itemAction) return;
            let act = ev.item.itemAction;
            if (act === "'about'") {
                actionStore.openAbout();
            } else if (act === "logout") {
                actionStore.logout({ psp: 'user' });
            } else if (navigationStore().findPath(act)) {
                let viewConfig = {
                    windowName: act,
                    perspectiveID: this.psp
                };
                if (act === 'AdminProfile') {
                    viewConfig.windowName = act + ev.item.itemIndex;
                    viewConfig.activeTab = ev.item.itemIndex;
                }
                navigationStore().open(viewConfig);
            }
        },
        async onLoad() {
            this.ddLoading = true;
            await ddStore.preloadGroup('COMMON');
            await ddStore.preloadGroup('DATA_GRID');
            this.ddLoading = false;
            this.ffList = await configStore.getAllClientFFList();
            let addControl = (controlToAdd, disabled) => {
                let tmpControl = {};
                let isDropdownButton = controlToAdd?.items?.length || controlToAdd?.isDropdownButton;
                tmpControl.type = isDropdownButton ? 'dropdownbutton' : 'button';
                if (isDropdownButton) {
                    tmpControl.items = controlToAdd.items;
                    tmpControl.fields = {
                        text: 'text',
                        value: 'value'
                    };
                    tmpControl.caretCss = 'hide';
                    tmpControl.onselect = 'doAction';
                    if (controlToAdd?.targetComponent) tmpControl.targetComponent = controlToAdd.targetComponent;
                }
                tmpControl.label = controlToAdd.text;
                tmpControl.hideLabel = true;
                tmpControl.title = controlToAdd.text;
                tmpControl.name = controlToAdd.value;
                tmpControl.disabled = disabled || false;
                // tmpControl.cssClass = 'e-flat nav-new-icon-btn';
                // if (!disabled) tmpControl.onclick = 'processTab';
                if (controlToAdd.onclick) tmpControl.onclick = controlToAdd.onclick;
                if (vmsIcons[tmpControl.name]) {
                    tmpControl.icon = {
                        name: vmsIcons[tmpControl.name],
                        size: "2xl"
                    };
                }
                let controlStoreIndex = createStoreIndex(this.storeId, tmpControl.name);
                if (typeof this.controlConfigs === 'undefined') this.controlConfigs = {};
                this.controlConfigs[tmpControl.name] = tmpControl;
                controlStore()
                    .addControl(tmpControl, this, controlStoreIndex);
                return tmpControl;
            };
            let excludeLeftMenu = ['COORD_GLOBAL_FILTERS', 'COORD_PRO', 'ADM_SIGN_IN_BLACKLIST'];
            let dashboardId = this.accountSettings['ACCOUNT_EC_DASHBOARD_ID'];
            if (!dashboardId) {
                excludeLeftMenu.push('COORD_DASHBOARD');
            }
            let selectedPspIsFound = false;
            let availableNavigations = navigationStore().availableNavigations;
            if (availableNavigations?.menu) {
                let tmpUserMenu = availableNavigations.menu.find((tmp) => tmp.name === 'M_ACCOUNT');
                if (Array.isArray(tmpUserMenu?.items)) {
                    let tmpItems = [];
                    tmpUserMenu.items.forEach((item) => {
                        tmpItems.push({
                            text: item.title,
                            value: item.name,
                            itemAction: item.action.act
                        });
                    });
                    this.userMenu = library.cloneObj(tmpItems);
                }
                let tmpHelpMenu = availableNavigations.menu.find((tmp) => tmp.name === 'M_HELP');
                if (Array.isArray(tmpHelpMenu?.items)) {
                    let tmpItems = [];
                    tmpHelpMenu.items.forEach((item) => {
                        if (item.ITEM_URL.indexOf('/custom/') === -1 && item.ITEM_URL.indexOf('devdash') === -1) {
                            tmpItems.push({
                                text: item.ITEM_TITLE,
                                value: item.action,
                                itemAction: item.action,
                                itemUrl: item.ITEM_URL
                            });
                        } else {
                            // @todo add to the settings
                        }
                    });
                    this.helpMenu = library.cloneObj(tmpItems);
                }
            }
            if (availableNavigations?.tabs) {
                if (dashboardId) {
                    this.leftMenu.push(addControl({
                        value: 'COORD_DASHBOARD',
                        text: 'Dashboard'
                    }));
                }
                for (let i in availableNavigations.tabs) {
                    if (excludeLeftMenu.indexOf(availableNavigations.tabs[i].FF_CONSTANT) === -1) {
                        if (availableNavigations.tabs[i].FF_CONSTANT === 'COORD_DASHBOARD') continue;
                        if (!selectedPspIsFound) {
                            if (this.psp && availableNavigations.tabs[i].FF_CONSTANT === ('COORD_' + this.psp).toUpperCase()) {
                                selectedPspIsFound = this.psp;
                            }
                            if (!this.psp && availableNavigations.tabs[i].FF_CONSTANT.indexOf('COORD_') !== -1) {
                                selectedPspIsFound = availableNavigations.tabs[i].FF_CONSTANT.replace('COORD_', '');
                            }
                        }
                        let tmpControl = {
                            value: availableNavigations.tabs[i].FF_CONSTANT,
                            text: availableNavigations.tabs[i].FF_NAME
                        };
                        this.leftMenu.push(addControl(tmpControl));
                    }
                }
            }
            if (availableNavigations?.adminTabs) {
                let tmpControl = {
                    text: 'Admin Options',
                    value: 'ADMIN_OPT'
                };
                let items = [];
                let tmpIdx = 0;
                for (let i in availableNavigations.adminTabs) {
                    if (excludeLeftMenu.indexOf(availableNavigations.adminTabs[i].FF_CONSTANT) === -1) {
                        items.push({
                            value: availableNavigations.adminTabs[i].FF_CONSTANT,
                            text: availableNavigations.adminTabs[i].FF_NAME,
                            itemAction: 'AdminProfile',
                            itemIndex: tmpIdx
                        });
                        tmpIdx++;
                    }
                }
                if (items.length) {
                    this.adminDrawerItems = library.cloneObj(items);
                    this.leftMenu.push(addControl(tmpControl));
                }
            }

            if (this.entity) {
                this.activeNavButton = 'COORD_' + this.entity.toUpperCase();
            }

            if (!this.entity) {
                if (dashboardId) {
                    this.$router.push({ path: '/dashboard' });
                } else if (selectedPspIsFound) {
                    this.$router.push({ path: '/' + selectedPspIsFound.toLowerCase() });
                }
            }
            navigationStore()
                .restoreUrl();
        },

    },
    components: {
        'nav-bar': defineAsyncComponent(() => import('./NavBar.vue')),
        'app-bar': defineAsyncComponent(() => import('./AppBar.vue')),
        'ejs-tooltip': defineAsyncComponent(() => import('@common/syncfusion/tooltip')),
        'ejs-dropdownbutton': defineAsyncComponent(() => import('@common/syncfusion/dropdownButton')),
    }
};

componentStore.initComponent(vmsCmp, vmsCfg);
export { vmsCfg, vmsCmp };
export default vmsCmp;
</script>

<style scoped>
header.vms-header > div:first-child {
    flex-shrink: 0;
    width: auto;
}

header.vms-header h1 {
    font-weight: 400;
    font-size: var(--text-size-h2);
    padding: 0 var(--space-6);
}

.vms-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-height: 3.75rem;
    background-color: var(--surface-default);
    border-bottom: 1px solid var(--border-color-default);
    padding: var(--space-1) var(--space-4);
    flex-shrink: 0;
}

.vms-header__section {
    display: flex;
    align-items: center;
}

.vms-header__section.user-menu__controls {
    justify-self: flex-end;
}

.vms-header__section-header:has(.header-image) {
    flex-shrink: 0;
}

.vms-header__section {
    display: flex;
    align-items: center;
}

.new-VMS-classes.main-grid-wrapper {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
}

.main-content-wrapper {
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    flex-grow: 12;
    overflow: hidden;
    min-height: 0;
    position: relative;
}

.header-image {
    height: 50px;
    width: auto;
    object-fit: contain;
}

.panel {
    padding: 10px;
}

.panel button {
    text-align: left;
    text-transform: none;
    font-weight: 500;
    font-size: var(--text-size-base);
    color: rgb(67, 67, 67);
}

.panel button .e-btn-icon {
    font-size: var(--text-size-h4);
    color: rgb(67, 67, 67);
    aspect-ratio: 1 / 1;
    width: 30px;
    margin-left: 0;
    text-align: center;
    font-weight: 300;
}

.panel-section > div {
    margin-top: 5px;
    margin-bottom: 5px;
}

.panel-section h4 {
    font-size: var(--text-size-h4);
    margin: 0 0 10px 0;
    color: #959595;
}

.toggle-panel {
    width: 20px;
    height: 20px;
    line-height: 20px;
    font-size: var(--text-size-h3);
    font-weight: 300;
    color: var(--text-color-disabled);
    position: absolute;
    bottom: 10px;
    margin: 0 auto;
    display: block;
    left: 0;
    right: 0;
}

.toggle-panel.opened {
    right: 10px;
    margin: unset;
    left: unset;
}

hr.panel-separator {
    border: none;
    height: 1px;
    background-color: var(--text-color-disabled);
    margin: 20px 0;
}

.main-grid-content {
    overflow: hidden;
    min-height: 0;
    height: 100%;
}

.main-grid-content > div {
    height: 100%;
}

.main-grid-content-inner {
    height: 100%;
    width: 100%;
}

.main-grid-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
}

.loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: vms-spin 0.7s linear infinite;
}

@keyframes vms-spin {
    to { transform: rotate(360deg); }
}

.content-fade-enter-active {
    transition: opacity 0.2s ease;
}

.content-fade-enter-from {
    opacity: 0;
}

.main-grid-content button {
    text-transform: none;
}

.separator:before {
    content: var(--pipe-separator);
    color: var(--text-color-disabled);
    margin: 0 var(--space-4);
}

</style>
